<template>
  <div>
    <h3 class="text-xl font-bold text-neutral-900">{{ $t('Blocked Users') }}</h3>
    <p class="text-neutral-600 mt-1">
      {{ $t('Block specific user accounts from posting, commenting, and voting on your project.') }}
    </p>

    <div
      v-if="project.allow_anonymous_posts"
      class="bg-orange-100 text-neutral-800 px-3 py-2 rounded mt-4 flex items-center"
    >
      <svg class="w-6 h-6 flex-shrink-0 mr-4 ml-2 text-orange-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
      <p class="text-sm">
        {{ $t('Blocking only works for authenticated users. Anonymous posts cannot be blocked since they have no account.') }}
      </p>
    </div>

    <div class="mt-6">
      <div class="mb-4">
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="$t('Search blocked users...')"
          class="input w-full"
        />
      </div>

      <div
        v-if="filteredBlockedUsers.length > 0"
        class="rounded-lg overflow-x-auto border text-sm"
      >
        <table class="table-auto w-full">
          <thead class="bg-neutral-100 border-b">
            <tr>
              <th class="p-2 text-left pl-4 w-5/12">{{ $t('User') }}</th>
              <th class="p-2 text-left w-3/12">{{ $t('Reason') }}</th>
              <th class="p-2 text-left w-2/12">{{ $t('Date Blocked') }}</th>
              <th class="p-2 text-right w-2/12 pr-4">{{ $t('Action') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="entry in filteredBlockedUsers"
              :key="entry.id"
              class="hover:bg-neutral-50"
            >
              <td class="p-2 pl-4 w-5/12">
                <div class="flex items-center">
                  <avatar
                    class="avatar mr-3 border-2 border-white shadow-md flex-shrink-0"
                    :size="32"
                    :src="entry.user.avatar"
                    :username="entry.user.name || entry.user.email"
                  ></avatar>
                  <div class="min-w-0">
                    <div class="font-medium text-neutral-800 truncate">{{ entry.user.name || 'Unknown' }}</div>
                    <div
                      class="text-neutral-500 text-xs truncate"
                      v-tippy="{
                        content: entry.user.email,
                        placement: 'bottom',
                        maxWidth: 'none',
                        allowHTML: false,
                        interactive: true,
                        appendTo: 'parent'
                      }"
                    >{{ entry.user.email }}</div>
                  </div>
                </div>
              </td>
              <td class="p-2 w-3/12 text-neutral-600 truncate">
                <span
                  v-if="entry.reason"
                  v-tippy="{
                    content: entry.reason,
                    placement: 'bottom',
                    maxWidth: '300px',
                    allowHTML: false,
                    interactive: true,
                    appendTo: 'parent'
                  }"
                >{{ entry.reason }}</span>
                <span v-else class="text-neutral-400">-</span>
              </td>
              <td class="p-2 w-2/12 text-neutral-600">{{ entry.created_at | humanDate }}</td>
              <td class="p-2 text-right w-2/12 pr-4">
                <a
                  href="#"
                  class="text-neutral-700 hover:text-neutral-900 hover:underline"
                  @click.prevent="entryToUnblock = entry"
                >{{ $t('Unblock') }}</a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else>
        <p class="text-neutral-700 text-sm mt-1 border border-dashed rounded-lg w-full p-2">
          {{ $t('No blocked users') }}
        </p>
      </div>

      <transition name="fromLeft" mode="out-in" appear>
        <p class="mb-1 text-neutral-700" v-if="isWorking">
          {{ $t('Working...') }}
        </p>
      </transition>

      <div class="mt-6 rounded-xl bg-neutral-100 p-4">
        <form action @submit.prevent="blockUser">
          <h4 class="font-semibold text-neutral-900 mb-2">{{ $t('Block a user') }}</h4>
          <div class="flex md:items-end flex-col md:flex-row items-stretch">
            <div class="md:mr-2 flex-1">
              <label class="label">{{ $t('Email address') }}</label>
              <input
                type="text"
                class="input"
                :placeholder="$t('user@example.com')"
                v-model="newEmail"
                :disabled="isWorking"
              />
            </div>
            <div class="md:mr-2 flex-1 mt-2 md:mt-0">
              <label class="label">{{ $t('Reason (optional)') }}</label>
              <input
                type="text"
                class="input"
                :placeholder="$t('e.g. Spam, abusive behavior')"
                v-model="newReason"
                :disabled="isWorking"
              />
            </div>
            <button
              type="submit"
              class="btn-main mt-2 md:mt-0 flex-shrink-0 justify-center"
              :disabled="isWorking"
            >
              {{ $t('Block User') }}
            </button>
          </div>
          <p v-if="errorMessage" class="text-red-500 text-sm mt-1">
            {{ errorMessage }}
          </p>
          <p v-if="successMessage" class="text-green-600 text-sm mt-1">
            {{ successMessage }}
          </p>
        </form>
      </div>
    </div>

    <DialogModel
      v-if="entryToUnblock"
      :title="$t('Confirmation')"
      :message="$t('Are you sure you want to unblock this user?')"
      :button-text="$t('Unblock')"
      tone="neutral"
      @done="unblockUser(entryToUnblock)"
      @cancel="entryToUnblock = null"
    />
  </div>
</template>

<script>
import Avatar from "vue-avatar";
import DialogModel from "../../components/dialog_model";

export default {
  components: { Avatar, DialogModel },
  props: {
    project: Object,
  },
  data() {
    return {
      blockedUsers: [],
      searchQuery: "",
      newEmail: "",
      newReason: "",
      errorMessage: "",
      successMessage: "",
      isWorking: false,
      entryToUnblock: null,
    };
  },
  computed: {
    filteredBlockedUsers() {
      if (!this.searchQuery) return this.blockedUsers;
      const query = this.searchQuery.toLowerCase();
      return this.blockedUsers.filter((entry) => {
        const name = (entry.user.name || "").toLowerCase();
        const email = (entry.user.email || "").toLowerCase();
        const reason = (entry.reason || "").toLowerCase();
        return name.includes(query) || email.includes(query) || reason.includes(query);
      });
    },
  },
  mounted() {
    this.fetchBlockedUsers();
  },
  methods: {
    fetchBlockedUsers() {
      Rails.ajax({
        url: `/blocked_project_users`,
        type: "GET",
        dataType: "json",
        beforeSend: function () {
          return true;
        },
        success: (data) => {
          this.blockedUsers = data;
        },
        error: (data) => {
          console.error("Error fetching blocked users:", data);
        },
      });
    },

    blockUser() {
      this.errorMessage = "";
      this.successMessage = "";

      if (!this.newEmail || !this.isEmailValid()) {
        this.errorMessage = this.$t('Please enter a valid email address.');
        return;
      }

      this.isWorking = true;
      let formData = new FormData();
      formData.append("email", this.newEmail);
      if (this.newReason) {
        formData.append("reason", this.newReason);
      }

      Rails.ajax({
        url: "/blocked_project_users",
        type: "POST",
        data: formData,
        dataType: "json",
        beforeSend: function () {
          return true;
        },
        success: (data) => {
          this.blockedUsers.unshift(data);
          this.newEmail = "";
          this.newReason = "";
          this.successMessage = this.$t('User has been blocked.');
          this.isWorking = false;
          setTimeout(() => { this.successMessage = ""; }, 3000);
        },
        error: (data) => {
          this.errorMessage = (data && data.error) || this.$t('Something went wrong. Please try again.');
          this.isWorking = false;
        },
      });
    },

    unblockUser(entry) {
      this.entryToUnblock = null;
      this.isWorking = true;
      Rails.ajax({
        url: `/blocked_project_users/${entry.id}`,
        type: "DELETE",
        dataType: "json",
        beforeSend: function () {
          return true;
        },
        success: () => {
          this.blockedUsers = this.blockedUsers.filter((b) => b.id !== entry.id);
          this.isWorking = false;
        },
        error: (data) => {
          this.errorMessage = (data && data.error) || this.$t('Failed to unblock user. Please try again.');
          this.isWorking = false;
        },
      });
    },

    isEmailValid() {
      return /^[\w.+-]+@[\w-]+\.[\.\w+-]*\w$/.test(this.newEmail);
    },
  },
  filters: {
    humanDate(date) {
      let event = new Date(date);
      let options = { year: "numeric", month: "long", day: "numeric" };
      return event.toLocaleDateString(undefined, options);
    },
  },
};
</script>

<style scoped>
tbody {
  display: block;
  max-height: 20rem;
  overflow-y: auto;
}

thead,
tbody tr {
  display: table;
  width: 100%;
  table-layout: fixed;
}
</style>
